import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Container,
  Grid,
  Segment,
  Table,
  Dimmer,
  Loader,
  Pagination,
  Form,
  Checkbox,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {
  getOutletSlotsAction,
  updateOutletsSlotsAction,
} from 'state/business/actions';
import { SemanticToastContainer } from 'react-semantic-toasts';
import DatePicker from 'components/DatePicker';
import { endOfDay } from 'date-fns';

const mapStateToPropsOutletSlotAction = ({ business }) => ({
  outletSlots: business.outletSlots,
  loadingOutlets: business.loadingOutlets,
  allBusiness: business.allBusiness,
});

const OutletSlotAction = connect(mapStateToPropsOutletSlotAction)(props => {
  const { dispatch, outletSlots, allBusiness, loadingOutlets } = props;
  const currBusinessId = allBusiness.data[allBusiness.activeIndex].id;
  const [actionState, setActionState] = useState({});

  useEffect(() => {
    const payload = {
      businessId: currBusinessId,
    };
    dispatch(getOutletSlotsAction(payload));
  }, [currBusinessId, dispatch]);

  useEffect(() => {
    const initialState = {
      newBillingCycle: {
        key: 'Keep the same billing cycle',
        text: 'Keep the same billing cycle',
        value: 'Keep the same billing cycle',
      },
      newExpirationDate: null,
      outletsSelectedStatus: (() => {
        if (outletSlots.data) {
          const outletStatus = {};
          for (let i = 0; i < outletSlots.data.outlet_slots.length; i++) {
            const outletId = parseInt(outletSlots.data.outlet_slots[i].id, 10);
            outletStatus[outletId] = false;
          }
          return outletStatus;
        }
        return {};
      })(),
      isSelectAll: false,
    };
    setActionState(initialState);
  }, [outletSlots.data]);

  /* Paging */
  let paging = null;
  if (outletSlots) {
    paging = outletSlots.paging;
  }
  let totalPages = 0;
  if (paging) totalPages = Math.ceil(paging.total / paging.size);

  const handleOnPageChange = (_, val) => {
    const { activePage } = val;
    const payload = {
      businessId: allBusiness.data[allBusiness.activeIndex].id,
      page: activePage,
      size: 10,
    };
    dispatch(getOutletSlotsAction(payload));
  };

  const handleNewExpirationDateChange = date => {
    setActionState({
      ...actionState,
      newExpirationDate: date ? endOfDay(date) : null,
    });
  };

  const handleSelectAll = () => {
    if (actionState.isSelectAll) {
      setActionState({
        ...actionState,
        outletsSelectedStatus: (() => {
          const newStatus = {};
          Object.keys(actionState.outletsSelectedStatus).forEach(id => {
            newStatus[id] = false;
          });
          return newStatus;
        })(),
        isSelectAll: false,
      });
    } else {
      setActionState({
        ...actionState,
        outletsSelectedStatus: (() => {
          const newStatus = {};
          Object.keys(actionState.outletsSelectedStatus).forEach(id => {
            newStatus[id] = true;
          });
          return newStatus;
        })(),
        isSelectAll: true,
      });
    }
  };

  const cntSelectedOutlet = () => {
    const cntSelected = Object.keys(actionState.outletsSelectedStatus).filter(
      id => actionState.outletsSelectedStatus[id] === true
    ).length;

    return cntSelected;
  };

  const handleSelectAnOutlet = outlet => {
    const outletId = parseInt(outlet.id, 10);
    const currStatus = actionState.outletsSelectedStatus[outletId];

    setActionState({
      ...actionState,
      isSelectAll:
        cntSelectedOutlet() + 1 === outletSlots.data.outlet_slots.length &&
        !actionState.outletsSelectedStatus[outletId],
      outletsSelectedStatus: {
        ...actionState.outletsSelectedStatus,
        [outletId]: !currStatus,
      },
    });
  };

  const handleSubmitOutletSlotsActionButton = () => {
    const payload = {
      business_id: currBusinessId,
      new_date: new Date(actionState.newExpirationDate).toISOString(),
      outlet_slot_ids: (() => {
        const checkedOutlets = Object.keys(
          actionState.outletsSelectedStatus
        ).filter(id => actionState.outletsSelectedStatus[id] === true);
        return checkedOutlets.map(key => parseInt(key, 10));
      })(),
    };
    dispatch(updateOutletsSlotsAction(payload));
  };

  const getAllCheckedOutlets = () => {
    const checkedOutlets = Object.keys(
      actionState.outletsSelectedStatus
    ).filter(id => actionState.outletsSelectedStatus[id] === true);

    return checkedOutlets;
  };

  const checkIsOkToSubmit = () => {
    const checkedOutlets = getAllCheckedOutlets();
    const { newBillingCycle, newExpirationDate } = actionState;
    return newBillingCycle && newExpirationDate && checkedOutlets.length > 0;
  };

  let isOkayToSubmit = false;
  if (Object.keys(actionState).length !== 0)
    isOkayToSubmit = checkIsOkToSubmit();

  const convertDate = payloadDate => {
    const dateTimeFormat = new Intl.DateTimeFormat('id', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const date = new Date(payloadDate);
    return dateTimeFormat.format(date);
  };

  return (
    <Container
      style={{ overflowY: 'scroll', maxHeight: '50vh', padding: '1vh' }}
    >
      <SemanticToastContainer />
      <Form>
        <Grid>
          <Grid.Column width="7">
            <label htmlFor="new-expiration-date">
              <strong>New Expiration Date</strong>
            </label>
            <DatePicker
              id="new-expiration-date"
              onChange={handleNewExpirationDateChange}
              selected={actionState.newExpirationDate}
              isClearable
            />
          </Grid.Column>
        </Grid>
      </Form>
      <br />
      {Object.keys(actionState).length !== 0 ? (
        <Segment style={{ padding: '0px' }}>
          <Dimmer active={loadingOutlets}>
            <Loader size="massive">Loading</Loader>
          </Dimmer>
          <Table celled selectable style={{ margin: '0px' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell
                  onClick={handleSelectAll}
                  style={{ cursor: 'pointer', color: 'blue' }}
                >
                  {actionState.isSelectAll ? 'Unselect All' : 'Select All'}
                </Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Created Date</Table.HeaderCell>
                <Table.HeaderCell>Expired Date</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {outletSlots.data ? (
                outletSlots.data.outlet_slots.map(outletSlot => {
                  let keys = '';
                  Object.keys(outletSlot).forEach(key => {
                    keys += outletSlot[key];
                  });
                  const checkSlotIsActive = expiredDate => {
                    const currTime = new Date().getTime();
                    const expiredTime = new Date(expiredDate).getTime();
                    return currTime <= expiredTime;
                  };
                  return (
                    <Table.Row key={keys}>
                      <Table.Cell>
                        <Checkbox
                          checked={
                            actionState.outletsSelectedStatus[outletSlot.id]
                          }
                          onClick={() => handleSelectAnOutlet(outletSlot)}
                        />
                      </Table.Cell>
                      <Table.Cell key={`${keys}Type`}>
                        {outletSlot.type}
                      </Table.Cell>
                      <Table.Cell key={`${keys}CreatedDate`}>
                        {convertDate(outletSlot.created_at)}
                      </Table.Cell>
                      <Table.Cell key={`${keys}ExpiredDate`}>
                        {convertDate(outletSlot.expired_at)}
                      </Table.Cell>
                      <Table.Cell key={`${keys}Status`}>
                        {checkSlotIsActive(outletSlot.expired_at) ? (
                          <p style={{ color: 'green' }}>Active</p>
                        ) : (
                          <p style={{ color: 'red' }}>Inactive</p>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              ) : (
                <></>
              )}
            </Table.Body>
            {paging ? (
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell colSpan="7">
                    <Pagination
                      activePage={paging.page}
                      totalPages={totalPages}
                      onPageChange={handleOnPageChange}
                      size="mini"
                    />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Footer>
            ) : (
              <></>
            )}
          </Table>
        </Segment>
      ) : (
        <></>
      )}
      <br />
      <Button
        type="button"
        floated="right"
        color="blue"
        onClick={handleSubmitOutletSlotsActionButton}
        disabled={!isOkayToSubmit}
      >
        Submit
      </Button>
      <br />
    </Container>
  );
});

OutletSlotAction.propTypes = {
  dispatch: PropTypes.func.isRequired,
  outletSlots: PropTypes.object.isRequired,
  loadingOutlets: PropTypes.bool.isRequired,
  allBusiness: PropTypes.object.isRequired,
};

export default OutletSlotAction;
